import React from "react";
import { cn } from "@/lib/utils";
import { Badge, type BadgeProps } from "@/components/shared/ui/badge";

/**
 * @deprecated Use `<Badge variant="...">` from
 * `@/components/shared/ui/badge` directly. This shim preserves the
 * legacy `color` prop surface so existing pages keep compiling while
 * the design-system migration rolls out. Do not add new callers.
 *
 * Mapping:
 *   green  -> success (brand-tinted)
 *   white  -> outline (neutral with border)
 *   yellow -> warning
 *   red    -> danger
 *   gray   -> neutral   (replaces the prior invisible gray-on-gray)
 */
type PillColor = "green" | "white" | "yellow" | "red" | "gray";

const COLOR_TO_VARIANT: Record<PillColor, BadgeProps["variant"]> = {
  green: "default",
  white: "outline",
  yellow: "warning",
  red: "danger",
  gray: "neutral",
};

interface PillProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: PillColor;
  children: React.ReactNode;
}

/**
 * Visual shim for legacy `<Pill>` consumers.
 *
 * Renders `<Badge>` with a larger text size (`text-sm`) and slightly more
 * padding than the default `Badge` to match the old sizing so tables/lists
 * don't reflow when this ships.
 */
const Pill: React.FC<PillProps> = ({
  color = "gray",
  className,
  children,
  ...props
}) => (
  <Badge
    variant={COLOR_TO_VARIANT[color] ?? "neutral"}
    className={cn("px-3 py-1 text-sm rounded-md font-medium", className)}
    {...props}
  >
    {children}
  </Badge>
);

export default Pill;
